import React, {useEffect, useState} from "react";
import axios from "config/axiosConfig";
import {useLocation, useNavigate} from "react-router-dom";
import {formatCurrency, formatDate} from "../util/helpers";

const statusOptions = [
    { value: "REQUESTED", label: "정산 요청" },
    { value: "COMPLETED", label: "정산 완료" },
    { value: "REJECTED", label: "정산 실패" },
];

const displayStatus = (status) => {
    switch (status) {
        case "REQUESTED": return "정산 요청";
        case "COMPLETED": return "정산 완료";
        case "REJECTED": return "정산 실패";
        default: return status || "-";
    }
};

export default function WithdrawalDetail() {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [formStatus, setFormStatus] = useState("");
    const [processedBy, setProcessedBy] = useState("");
    const [memo, setMemo] = useState("");
    const [submitState, setSubmitState] = useState({ loading: false, success: null, error: null });

    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const id = queryParams.get("id");

    useEffect(() => {
        if (!id) {
            setError("유효한 출금 요청 ID가 없습니다.");
            return;
        }

        const fetchDetail = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`/admin/v1/wallet/withdrawals/${id}`);
                const data = response.data;
                setDetail(data);
                setFormStatus(data.status || "");
                setProcessedBy(data.processedBy || "");
                setMemo(data.memo || "");
                setSubmitState({ loading: false, success: null, error: null });
                setError(null);
            } catch (err) {
                setError(err?.response?.data?.detail || "출금 정보를 불러오는 중 오류가 발생했습니다.");
            } finally {
                setLoading(false);
            }
        };

        fetchDetail();
    }, [id]);

    const handleStatusSubmit = async (event) => {
        event.preventDefault();
        if (!detail || !formStatus) {
            return;
        }

        try {
            setSubmitState({ loading: true, success: null, error: null });
            const response = await axios.patch(`/admin/v1/wallet/withdrawals/${detail.id}/status`, {
                status: formStatus,
                processedBy: processedBy?.trim() || null,
                memo: memo?.trim() || null,
            });
            const data = response.data;
            setDetail(data);
            setFormStatus(data.status || "");
            setProcessedBy(data.processedBy || "");
            setMemo(data.memo || "");
            setSubmitState({ loading: false, success: "출금 상태를 변경했습니다.", error: null });
        } catch (err) {
            setSubmitState({
                loading: false,
                success: null,
                error: err?.response?.data?.detail || "출금 상태 변경 중 오류가 발생했습니다."
            });
        }
    };

    const renderCurrency = (value) => {
        if (value === null || value === undefined) {
            return "-";
        }
        return value === 0 ? "0" : formatCurrency(value);
    };

    const renderDetail = () => {
        if (loading) {
            return <div className="text-center py-4">불러오는 중...</div>;
        }
        if (error) {
            return <div className="alert alert-danger" role="alert">{error}</div>;
        }
        if (!detail) {
            return null;
        }

        const targetDisplay = detail.targetMemberNickname
            ? `${detail.targetMemberNickname} (ID: ${detail.targetMemberId})`
            : detail.targetMemberId ? `ID: ${detail.targetMemberId}` : "-";

        return (
            <>
                <table className="table table-bordered">
                    <tbody>
                    <tr>
                        <th style={{width: "200px"}}>정산요청ID</th>
                        <td>{detail.id}</td>
                    </tr>
                    <tr>
                        <th>결제ID</th>
                        <td>{detail.paymentId || "-"}</td>
                    </tr>
                    <tr>
                        <th>결제 금액</th>
                        <td>{renderCurrency(detail.paymentAmount)}</td>
                    </tr>
                    <tr>
                        <th>수수료</th>
                        <td>{renderCurrency(detail.commissionAmount)}</td>
                    </tr>
                    <tr>
                        <th>출금 금액</th>
                        <td>{renderCurrency(detail.withdrawalAmount)}</td>
                    </tr>
                    <tr>
                        <th>출금 상태</th>
                        <td>{displayStatus(detail.status)}</td>
                    </tr>
                    <tr>
                        <th>출금 대상</th>
                        <td>{targetDisplay}</td>
                    </tr>
                    <tr>
                        <th>계좌주</th>
                        <td>{detail.accountOwner || "-"}</td>
                    </tr>
                    <tr>
                        <th>은행</th>
                        <td>{detail.bankName || "-"} {detail.bankCode ? `(${detail.bankCode})` : ""}</td>
                    </tr>
                    <tr>
                        <th>계좌번호</th>
                        <td>{detail.accountNumber || "-"}</td>
                    </tr>
                    <tr>
                        <th>출금 요청일시</th>
                        <td>{formatDate(detail.requestedAt)}</td>
                    </tr>
                    <tr>
                        <th>출금 처리일시</th>
                        <td>{formatDate(detail.processedAt) || "-"}</td>
                    </tr>
                    <tr>
                        <th>처리자</th>
                        <td>{detail.processedBy || "-"}</td>
                    </tr>
                    <tr>
                        <th>메모</th>
                        <td style={{whiteSpace: "pre-line"}}>{detail.memo || "-"}</td>
                    </tr>
                    </tbody>
                </table>

                <hr className="my-4"/>

                <form onSubmit={handleStatusSubmit}>
                    <div className="form-group">
                        <label>출금 상태</label>
                        <select
                            className="form-control"
                            value={formStatus}
                            onChange={e => setFormStatus(e.target.value)}
                            required
                        >
                            <option value="" disabled>상태를 선택하세요</option>
                            {statusOptions.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label>처리자 (선택)</label>
                        <input
                            type="text"
                            className="form-control"
                            value={processedBy}
                            onChange={e => setProcessedBy(e.target.value)}
                            placeholder="처리자 이름 또는 ID를 입력하세요"
                        />
                    </div>

                    <div className="form-group">
                        <label>메모 (선택)</label>
                        <textarea
                            className="form-control"
                            rows={3}
                            value={memo}
                            onChange={e => setMemo(e.target.value)}
                            placeholder="상세 메모를 남겨주세요."
                        />
                    </div>

                    {submitState.error && (
                        <div className="alert alert-danger mt-3" role="alert">{submitState.error}</div>
                    )}
                    {submitState.success && (
                        <div className="alert alert-success mt-3" role="alert">{submitState.success}</div>
                    )}

                    <div className="mt-3 d-flex">
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={submitState.loading || !formStatus}
                        >
                            {submitState.loading ? "변경 중..." : "상태 변경"}
                        </button>
                    </div>
                </form>
            </>
        );
    };

    return (
        <div className="App">
            <div className="wrapper">
                <div className="content-wrapper" style={{marginLeft: '0px'}}>
                    <div className="content-header">
                        <div className="container-fluid">
                            <div className="row mb-2">
                                <div className="col-sm-12">
                                    <ol className="breadcrumb">
                                        <li className="breadcrumb-item active">결제관리 &gt; 출금 요청 &gt; 상세</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>

                    <section className="content">
                        <div className="container-fluid">
                            <div className="row">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-header d-flex justify-content-between align-items-center">
                                            <h3 className="card-title" style={{fontWeight: 'bold'}}>출금 상세 정보</h3>
                                            <button
                                                type="button"
                                                className="btn btn-secondary btn-sm"
                                                onClick={() => navigate(-1)}
                                            >
                                                목록으로
                                            </button>
                                        </div>
                                        <div className="card-body">
                                            {renderDetail()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}
